import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { filterVisibleSubtasks } from "@/lib/visibility";
import { getNotifPrefs, applyServerNotifPrefs } from "@/lib/notifPrefs";
import { showAppNotification, subscribeToPush } from "@/lib/notify";

const DataContext = createContext(null);

const byName = (a, b) =>
  (a?.name || "").localeCompare(b?.name || "", "it", { sensitivity: "base" });

export function DataProvider({ children }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Task-creation/edit panel guard: while a task panel is open we suspend the
  // "Aggiorna task" auto-refresh timer so it doesn't reload data under the user.
  const taskPanelCountRef = useRef(0);
  const setTaskPanelOpen = useCallback((open) => {
    taskPanelCountRef.current += open ? 1 : -1;
    if (taskPanelCountRef.current < 0) taskPanelCountRef.current = 0;
  }, []);
  const isTaskPanelOpen = useCallback(() => taskPanelCountRef.current > 0, []);

  // Browser notifications: single source of truth to avoid duplicates.
  // notifiedRef stays null until the first list is seen, then holds the set of
  // ids we've already handled. Pre-existing notifications are seeded (not fired).
  const notifiedRef = useRef(null);
  const fireBrowserNotifs = useCallback((list) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const prefs = getNotifPrefs();
    if (notifiedRef.current === null) {
      // First time we see the list: remember everything, don't fire for old items
      notifiedRef.current = new Set((list || []).map((n) => n.id));
      return;
    }
    for (const n of list || []) {
      if (notifiedRef.current.has(n.id)) continue;
      notifiedRef.current.add(n.id);
      if (!n.read && prefs.browser && prefs.task) {
        const titles = { assigned: "Nuova task assegnata", completed: "Task completata" };
        const title = titles[n.type] || "Hi-Work";
        showAppNotification(title, n.message, { tag: n.id, url: "/il-mio-lavoro" });
      }
    }
  }, []);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [u, c, t, n] = await Promise.all([
        api.get("/users"),
        api.get("/clients"),
        api.get("/tasks"),
        api.get("/notifications"),
      ]);
      setUsers(u.data);
      setClients([...c.data].sort(byName));
      setTasks(t.data);
      setNotifications(n.data);
      fireBrowserNotifs(n.data);
    } finally {
      setLoading(false);
    }
  }, [user, fireBrowserNotifs]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  // Reset dedupe + state when the logged-in user changes
  useEffect(() => {
    notifiedRef.current = null;
  }, [user]);

  // Subscribe this device to web push (so notifications arrive in the
  // installed PWA even when the window is in the background).
  useEffect(() => {
    if (!user) return;
    // Apply notification preferences saved on the account (valid on all devices)
    if (user.notif_prefs) applyServerNotifPrefs(user.notif_prefs);
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      subscribeToPush(api);
    }
  }, [user]);

  // Poll notifications every 20s and fire browser notifications once per new item
  useEffect(() => {
    if (!user) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/notifications");
        fireBrowserNotifs(data);
        setNotifications(data);
      } catch {
        /* ignore */
      }
    }, 20000);
    return () => clearInterval(t);
  }, [user, fireBrowserNotifs]);

  // Users
  const addUser = async (payload) => {
    const { data } = await api.post("/users", payload);
    setUsers((p) => [...p, data]);
    return data;
  };
  const deleteUser = async (id) => {
    await api.delete(`/users/${id}`);
    setUsers((p) => p.filter((u) => u.id !== id));
    setTasks((p) =>
      p.map((t) => ({ ...t, assignee_ids: t.assignee_ids.filter((a) => a !== id) })),
    );
  };

  const updateUser = async (id, patch) => {
    const { data } = await api.patch(`/users/${id}`, patch);
    setUsers((p) => p.map((u) => (u.id === id ? data : u)));
    return data;
  };

  // Clients
  const addClient = async (payload) => {
    const { data } = await api.post("/clients", payload);
    setClients((p) => [...p, data].sort(byName));
    return data;
  };
  const updateClient = async (id, patch) => {
    const { data } = await api.patch(`/clients/${id}`, patch);
    setClients((p) => p.map((c) => (c.id === id ? data : c)).sort(byName));
    return data;
  };
  const deleteClient = async (id) => {
    await api.delete(`/clients/${id}`);
    setClients((p) => p.filter((c) => c.id !== id));
    setTasks((p) => p.filter((t) => t.client_id !== id));
  };

  // Tasks
  const addTask = async (payload) => {
    const { data } = await api.post("/tasks", payload);
    setTasks((p) => [data, ...p]);
    return data;
  };
  const updateTask = async (id, patch) => {
    const { data } = await api.patch(`/tasks/${id}`, patch);
    setTasks((p) => p.map((t) => (t.id === id ? data : t)));
    // If this update transitioned task into 'completato', refresh notifications immediately
    // and re-fetch tasks (in case a recurring child was spawned server-side)
    if (patch.status === "completato") {
      try {
        const [n, tasksRes] = await Promise.all([
          api.get("/notifications"),
          api.get("/tasks"),
        ]);
        fireBrowserNotifs(n.data);
        setNotifications(n.data);
        setTasks(tasksRes.data);
      } catch {
        /* ignore */
      }
    }
    return data;
  };
  const deleteTask = async (id) => {
    await api.delete(`/tasks/${id}`);
    setTasks((p) => p.filter((t) => t.id !== id));
  };

  const addComment = async (taskId, text) => {
    const { data } = await api.post(`/tasks/${taskId}/comments`, { text });
    setTasks((p) =>
      p.map((t) =>
        t.id === taskId
          ? { ...t, comments_count: (t.comments_count || 0) + 1 }
          : t,
      ),
    );
    return data;
  };

  // Notifications
  const markNotifRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setNotifications((p) => p.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };
  const markAllNotifRead = async () => {
    await api.post(`/notifications/read-all`);
    setNotifications((p) => p.map((n) => ({ ...n, read: true })));
  };
  const deleteNotif = async (id) => {
    await api.delete(`/notifications/${id}`);
    setNotifications((p) => p.filter((n) => n.id !== id));
    if (notifiedRef.current) notifiedRef.current.delete(id);
  };
  const clearAllNotifs = async () => {
    await api.delete(`/notifications`);
    setNotifications([]);
    if (notifiedRef.current) notifiedRef.current.clear();
  };

  // Filter out sub-tasks whose parent isn't yet completed, and trashed tasks
  // (trashed tasks live only in the Cestino, which reads rawTasks).
  const visibleTasks = useMemo(
    () => filterVisibleSubtasks(tasks).filter((t) => !t.trashed),
    [tasks],
  );

  return (
    <DataContext.Provider
      value={{
        users,
        clients,
        tasks: visibleTasks,
        rawTasks: tasks,
        notifications,
        loading,
        reload: load,
        setTaskPanelOpen,
        isTaskPanelOpen,
        addUser,
        deleteUser,
        updateUser,
        addClient,
        updateClient,
        deleteClient,
        addTask,
        updateTask,
        deleteTask,
        addComment,
        markNotifRead,
        markAllNotifRead,
        deleteNotif,
        clearAllNotifs,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  return useContext(DataContext);
}
